import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    TemplateRef,
} from '@angular/core';

import { FractionColours } from './models/floa.model';

@Component({
    selector: 'fraction-bar',
    template: `
        <div
            class="fraction-container"
            [style.backgroundColor]="styles.backgroundColour"
            [style.width]="barWidth"
        >
            <div
                class="max-bar-container left"
                [style.width]="maxnumwidth"
            >
                <ng-container *ngIf="showHoverTooltip == true; else noHoverNum">
                    <div
                        class="bar-label-container"
                        [style.width]="numwidth"
                    >
                        <div class="numerator-label">
                            {{
                                numeratorLabel != 0
                                    ? (numeratorLabel | number: '1.0-0')
                                    : ''
                            }}
                        </div>
                        <div
                            class="numerator-bar"
                            hoverTooltip
                            [hoverTooltipContent]="hoverTemplate"
                            [hoverTooltipData]="hoverData"
                            [ngClass]="numeratorClass"
                        ></div>
                    </div>
                </ng-container>
                <ng-template #noHoverNum>
                    <div
                        class="bar-label-container"
                        [style.width]="numwidth"
                    >
                        <div class="numerator-label">
                            {{
                                numeratorLabel != 0
                                    ? (numeratorLabel | number: '1.0-0')
                                    : ''
                            }}
                        </div>
                        <div
                            class="numerator-bar"
                            [ngClass]="numeratorClass"
                            [style.width]="numwidth"
                        ></div>
                    </div>
                </ng-template>
            </div>

            <div
                class="max-bar-container right"
                [style.width]="maxdenomwidth"
            >
                <ng-container
                    *ngIf="showHoverTooltip == true; else noHoverDenom"
                >
                    <div
                        class="bar-label-container"
                        [style.width]="denomwidth"
                    >
                        <div class="denominator-label">
                            {{
                                denominatorLabel != 0
                                    ? (denominatorLabel | number: '1.0-0')
                                    : ''
                            }}
                        </div>
                        <div
                            class="denominator-bar"
                            hoverTooltip
                            [hoverTooltipContent]="hoverTemplate"
                            [hoverTooltipData]="hoverData"
                            [ngClass]="denominatorClass"
                        ></div>
                    </div>
                </ng-container>
                <ng-template #noHoverDenom>
                    <div
                        class="bar-label-container"
                        [style.width]="denomwidth"
                    >
                        <div class="denominator-label">
                            {{
                                denominatorLabel != 0
                                    ? (denominatorLabel | number: '1.0-0')
                                    : ''
                            }}
                        </div>
                        <div
                            class="denominator-bar"
                            [ngClass]="denominatorClass"
                            [style.width]="denomwidth"
                        ></div>
                    </div>
                </ng-template>
            </div>
        </div>
    `,
})
export class FractionBarComponent implements OnInit {
    @Input() numerator: number;
    @Input() denominator: number;
    @Input() divisor: number;
    @Input() numeratorLabel: any;
    @Input() denominatorLabel: any;
    @Input() maxNumerator: number;
    @Input() maxDenominator: number;
    @Input() numeratorClass = 'green';
    @Input() denominatorClass = 'red';
    @Input() styles: FractionColours = {};
    @Input() barWidth = '';
    @Input() showHoverTooltip = false;

    @Input() hoverTemplate: TemplateRef<any>;
    @Input() hoverData: any;

    @Output() public click: EventEmitter<any>;

    numwidth: string;
    denomwidth: string;

    maxnumwidth: string;
    maxdenomwidth: string;

    hoverdata: any;
    showHoverContainer = false;

    mouseLeft: number;

    constructor(public elRef: ElementRef) {
        this.click = new EventEmitter<any>();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args) {
        // eslint-disable-next-line no-prototype-builtins
        if (
            args &&
            // eslint-disable-next-line no-prototype-builtins
            (args.hasOwnProperty('numerator') ||
                // eslint-disable-next-line no-prototype-builtins
                args.hasOwnProperty('denominator'))
        ) {
            this.recalculate();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        if (this.numerator != null && this.denominator != null) {
            this.recalculate();
        }
    }

    @HostListener('click', ['$event'])
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    triggerClick(event) {
        if (event) {
            this.click.emit();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    recalculate() {
        // for subordinates
        if (this.maxNumerator != null && this.maxDenominator != null) {
            this.setSubordinateDimensions();
        } else {
            this.setLeaderDimensions();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setSubordinateDimensions() {
        const numeratorwidth = (this.numerator / this.maxNumerator) * 100;
        const denominatorwidth = (this.denominator / this.maxDenominator) * 100;
        const maxnumeratorwidth = (this.maxNumerator / this.divisor) * 100;
        const maxdenominatorwidth = (this.maxDenominator / this.divisor) * 100;

        this.maxdenomwidth =
            typeof maxdenominatorwidth === 'number' &&
            maxdenominatorwidth !== Infinity &&
            maxdenominatorwidth !== -Infinity
                ? `${maxdenominatorwidth.toString()}%`
                : '0%';
        this.maxnumwidth =
            typeof maxnumeratorwidth === 'number' &&
            maxnumeratorwidth !== Infinity &&
            maxnumeratorwidth !== -Infinity
                ? `${maxnumeratorwidth.toString()}%`
                : '0%';
        this.numwidth =
            typeof numeratorwidth === 'number' &&
            numeratorwidth !== Infinity &&
            numeratorwidth !== -Infinity
                ? `${numeratorwidth.toString()}%`
                : '0%';
        this.denomwidth =
            typeof denominatorwidth === 'number' &&
            denominatorwidth !== Infinity &&
            denominatorwidth !== -Infinity
                ? `${denominatorwidth.toString()}%`
                : '0%';
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setLeaderDimensions() {
        const num = (this.numerator / this.divisor) * 100;
        const dnom = (this.denominator / this.divisor) * 100;

        this.maxnumwidth =
            typeof num === 'number' && num !== Infinity && num !== -Infinity
                ? `${num.toString()}%`
                : '0%';
        this.maxdenomwidth =
            typeof dnom === 'number' && dnom !== Infinity && dnom !== -Infinity
                ? `${dnom.toString()}%`
                : '0%';
        this.numwidth = '100%';
        this.denomwidth = '100%';
    }
}
